import { graphql } from 'gatsby';
import React from 'react';
import Container from '../../primitives/grid/container';
import FnolForm from './fnol';
import FranchiseOwnershipForm from './franchise-ownership';
import FirstResponderForm from './first-responder';
import CovidCleaningForm from './covid-cleaning';
import CeForm from './continuing-education';
import PreferredVendorForm from './preferred-vendor';
import FeedbackForm from './feedback-form';
import { colors } from '../../primitives/colors';
import RoofingForm from './roofing';
import RichTextEditorStyles from '../../utils/rich-text-editor-styles';
import apCaseOnlyTitleTags from '../../utils/ap-case-only-title-tags';
import paddingDefault from '../../utils/padding-default';
import Typography from '../../primitives/typography';
import { breakpoints } from '../../primitives/tokens';
import Col from '../../primitives/grid/col';
import Row from '../../primitives/grid/row';

interface FormBuilderProps {
    successMessage?: string
    formId: string
    submitButton?: string
}

export const FormBuilder = ({ successMessage, formId, submitButton }: FormBuilderProps): JSX.Element => {
    switch (formId) {
        case 'roofing':
            return (
                <RoofingForm
                    successMessage={successMessage}
                    submitButton={submitButton}
                />
            );
        case 'ce-form':
            return <CeForm submitButton={submitButton} />;
        case 'covid-cleaning-form':
            return (
                <CovidCleaningForm
                    successMessage={successMessage}
                    submitButton={submitButton}
                />
            );
        case 'first-responder-form':
            return (
                <FirstResponderForm
                    successMessage={successMessage}
                    submitButton={submitButton}
                />
            );
        case 'preferred-vendor-form':
            return (
                <PreferredVendorForm
                    successMessage={successMessage}
                    submitButton={submitButton}
                />
            );
        case 'franchise-ownership-form':
            return (
                <FranchiseOwnershipForm
                    submitButtonText={submitButton}
                    successMessageText={successMessage}
                />
            );
        case 'fnol-form':
            return (
                <FnolForm submitButtonText={submitButton} successMessageText={successMessage} />
            );
        case 'feedback-form':
            return (
                <FeedbackForm submitButton={submitButton} successMessage={successMessage} />
            );
        default:
            return (
                <code>
                    Missing Form:
                    {formId}
                </code>
            );
    }
};

const Form: React.FC<Queries.FormDataFragment> = (data): JSX.Element => {
    const {
        headline,
        copy,
        spacing,
        formId,
        submitButtonLabel,
        thankYouMessage,
        settings,
    } = data;

    const hideBackground = settings?.hideBackground;

    return (
        <div
            css={{
                ...paddingDefault(spacing),
                background: hideBackground ? colors.shadesWhite : colors.grey100,
            }}
        >
            <Container css={{
                position: 'relative',
                paddingTop: '4px',
            }}
            >
                {(headline || copy) && (
                    <Row css={{ marginBottom: '32px' }}>
                        <Col breakpoints={{ dt: { span: 12 } }}>
                            <Typography
                                as="h2"
                                fontWeight="regular"
                                reducedLetterSpacing
                                fontSize={{
                                    dt: '4xl',
                                    tb: '3xl',
                                }}
                                css={[{
                                    color: colors.shadesBlack,
                                    marginBottom: copy ? '16px' : '0',
                                    [breakpoints.tb]: {
                                        textAlign: 'left',
                                        maxWidth: '80%',
                                    },
                                }]}
                            >
                                {apCaseOnlyTitleTags(headline)}
                            </Typography>
                        </Col>
                        {copy && (
                            <Col breakpoints={{ dt: { span: 8 } }}>
                                <div
                                    css={RichTextEditorStyles}
                                    /* eslint-disable-next-line react/no-danger */
                                    dangerouslySetInnerHTML={{ __html: copy || '' }}
                                />
                            </Col>
                        )}
                    </Row>
                )}
            </Container>
            <Container>
                {formId && (
                    <FormBuilder
                        successMessage={thankYouMessage || ''}
                        formId={formId}
                        submitButton={submitButtonLabel ?? undefined}
                    />
                )}
            </Container>
        </div>
    );
};

export const query = graphql`
  fragment FormData on CsForm {
    ... on CsForm {
      headline
      copy
      formId
      submitButtonLabel
      thankYouMessage
      spacing {
        topGutter
        bottomGutter
      }
      settings {
        hideBackground
      }
    }
  }
`;

export default Form;
